/**
 * Cross-category cross-sell picker.
 *
 * Exposes window.HE.getCrossSellPicks({ productId, productType, productCollections, limit })
 * which returns a Promise<Product[]>. Used by:
 *   - sections/section-frequently-bought-together.liquid (PDP FBT recs)
 *   - assets/post-atc-upsell.js (post-ATC modal recs)
 *
 * The goal is to avoid recommending another product in the SAME category the
 * shopper is already looking at. Mapping is in assets/cross-sell-map.json —
 * merchant-editable. Default strategy: view a gummy → recommend disposables +
 * prerolls; view a disposable → recommend edibles + prerolls; etc.
 *
 * Priority chain:
 *   1. Match product.type against by_type  → fetch from mapped target collections
 *   2. Match product.collections (first hit wins) against by_collection
 *   3. Shopify's own /recommendations/products.json?intent=complementary
 *      (honours merchant-curated complementary products set via product metafields)
 *   4. Hard fallback: the "fallback" collections from the map (default: "all")
 *
 * Throughout: dedupe by product ID, exclude the anchor product, shuffle within
 * each collection for variety, short-circuit once `limit` is reached.
 */
(function () {
  if (typeof window === 'undefined') return;
  if (window.HE && typeof window.HE.getCrossSellPicks === 'function') return;

  var mapPromise = null;

  function loadMap() {
    if (mapPromise) return mapPromise;
    var url = window.HE_CROSS_SELL_MAP_URL;
    if (!url) {
      console.warn('[he-cross-sell] window.HE_CROSS_SELL_MAP_URL not set; using empty map');
      mapPromise = Promise.resolve({ by_type: {}, by_collection: {}, fallback: ['all'] });
      return mapPromise;
    }
    mapPromise = fetch(url)
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function (err) {
        if (window.HE && window.HE.trackError) {
          window.HE.trackError('he-cross-sell/loadMap', err, { url: url });
        }
        return null;
      })
      .then(function (raw) {
        return raw || { by_type: {}, by_collection: {}, fallback: ['all'] };
      });
    return mapPromise;
  }

  function jsonFetch(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function (err) {
        if (window.HE && window.HE.trackError) {
          window.HE.trackError('he-cross-sell/jsonFetch', err, { url: url });
        }
        return null;
      });
  }

  function shuffle(arr) {
    var copy = arr.slice();
    for (var i = copy.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = copy[i];
      copy[i] = copy[j];
      copy[j] = tmp;
    }
    return copy;
  }

  function normalizeId(v) {
    if (v == null) return null;
    var n = typeof v === 'number' ? v : parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  }

  function pushUnique(picks, candidates, limit, excludeIds) {
    for (var i = 0; i < candidates.length && picks.length < limit; i++) {
      var p = candidates[i];
      if (!p || !p.id) continue;
      var pid = normalizeId(p.id);
      if (pid == null) continue;
      if (excludeIds.has(pid)) continue;
      picks.push(p);
      excludeIds.add(pid);
    }
  }

  /**
   * Fetch products from each collection handle in order, shuffle the results,
   * push uniques until we hit the limit. Stops early once limit reached.
   */
  function fetchFromCollections(handles, limit, excludeIds) {
    if (!Array.isArray(handles) || handles.length === 0) return Promise.resolve([]);
    var picks = [];
    var chain = Promise.resolve();
    handles.forEach(function (handle) {
      chain = chain.then(function () {
        if (picks.length >= limit) return null;
        return jsonFetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=20')
          .then(function (json) {
            if (!json) return;
            var products = shuffle(json.products || []);
            pushUnique(picks, products, limit, excludeIds);
          });
      });
    });
    return chain.then(function () { return picks; });
  }

  async function getCrossSellPicks(opts) {
    opts = opts || {};
    var limit = typeof opts.limit === 'number' ? opts.limit : 2;
    var anchorId = normalizeId(opts.productId);
    var productType = (opts.productType || '').trim();
    var productCollections = Array.isArray(opts.productCollections)
      ? opts.productCollections.filter(Boolean)
      : [];

    var excludeIds = new Set();
    if (anchorId != null) excludeIds.add(anchorId);

    var map = await loadMap();
    var byType = (map && map.by_type) || {};
    var byCollection = (map && map.by_collection) || {};
    var fallback = (map && map.fallback) || ['all'];

    var picks = [];

    // Strategy 1 — match by product.type
    if (productType && byType[productType] && picks.length < limit) {
      var typePicks = await fetchFromCollections(byType[productType], limit, excludeIds);
      pushUnique(picks, typePicks, limit, excludeIds);
    }

    // Strategy 2 — walk the product's collections, first hit wins
    if (picks.length < limit && productCollections.length > 0) {
      for (var i = 0; i < productCollections.length; i++) {
        if (picks.length >= limit) break;
        var handle = productCollections[i];
        if (!byCollection[handle]) continue;
        var collPicks = await fetchFromCollections(byCollection[handle], limit, excludeIds);
        pushUnique(picks, collPicks, limit, excludeIds);
      }
    }

    // Strategy 3 — Shopify's merchant-curated complementary recs
    if (picks.length < limit && anchorId != null) {
      var compJson = await jsonFetch(
        '/recommendations/products.json?product_id=' + encodeURIComponent(anchorId) +
        '&limit=' + (limit * 2) + '&intent=complementary'
      );
      if (compJson) pushUnique(picks, (compJson.products || []), limit, excludeIds);
    }

    // Strategy 4 — hard fallback collections (default: "all")
    if (picks.length < limit && Array.isArray(fallback) && fallback.length > 0) {
      var fbPicks = await fetchFromCollections(fallback, limit, excludeIds);
      pushUnique(picks, fbPicks, limit, excludeIds);
    }

    return picks.slice(0, limit);
  }

  window.HE = window.HE || {};
  window.HE.getCrossSellPicks = getCrossSellPicks;
})();
